import { useQuery } from "@tanstack/react-query";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchServices } from "../services/servicesService";
import { SERVICES_QUERY_KEY } from "./useServices";

export type ServicesCreatedStatus = "loading" | "ready" | "failed";

export interface ServicesCreatedState {
  /**
   * Whether the workspace has a service anyone could be recorded against.
   * `false` until the answer is in.
   */
  servicesCreated: boolean;
  status: ServicesCreatedStatus;
}

/**
 * Whether the workspace has a listed service — one bit, the way
 * `usePlansCreated` answers the same question about plans.
 *
 * Asked by the gate over Add client and New enquiry: both forms' Goal field
 * picks from the practice's own active services and sends the service's
 * `public_id`, so a workspace with none has a form nobody can finish. See
 * `useSetupGate`.
 *
 * Active only, and that is the whole of the difference from counting the list:
 * a deactivated service is off the public page and out of the Goal field —
 * `fetchServiceGoals` asks for `?is_active=true` — so a workspace holding
 * nothing but those is in exactly the position this is about. The same
 * distinction the Add client form draws over plans with `sellable`.
 *
 * Under `useServices`' own query key rather than a key of its own, unlike
 * `usePlansCreated`: this is the identical request the Services tab makes —
 * one unpaged array of every service — so the two share the one cache entry
 * instead of putting the same call on the wire twice. `usePlansCreated` cannot,
 * since it reads one page of a differently-shaped response.
 *
 * Quiet on failure. The toast belongs to `useServices`, which is mounted when
 * somebody is actually looking at their services; nobody came to the roster to
 * read about them. A `failed` leaves the gate open — not knowing is not the
 * same as knowing there is nothing there.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useServicesCreated(): ServicesCreatedState {
  const getToken = useTokenGetter();

  const query = useQuery({
    queryKey: SERVICES_QUERY_KEY,
    queryFn: ({ signal }) => fetchServices(getToken, signal),
  });

  return {
    servicesCreated: (query.data ?? []).some(
      (service) => service.status === "Active"
    ),
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
  };
}

export default useServicesCreated;
